import type { ReactNode } from 'react';

/** Canonical public web origin (matches BASE_URL in pages/+Head.tsx). */
export const RAS_WEB_ORIGIN = 'https://www.raspicks.com';

/**
 * Absolute URL to the web subscribe/checkout page, tagged so orders that start
 * from the iOS app's external link are attributable (channel-based UTM, per the
 * repo's tag-by-channel convention).
 *
 * `path` targets a specific product page (e.g. '/order/nfl'); defaults to the
 * order landing '/order'.
 */
export function subscribeWebUrl(path = '/order'): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${RAS_WEB_ORIGIN}${p}?utm_source=ios-app&utm_medium=external-link`;
}

type Props = {
  /** Product path on the web, e.g. '/order/nfl'. Defaults to '/order'. */
  path?: string;
  className?: string;
  children: ReactNode;
  'aria-label'?: string;
};

/**
 * The iOS reader-app compliant "Subscribe at raspicks.com" affordance.
 *
 * iOS hides in-app web checkout (isIosNative) so the app never sells digital
 * goods inside the WebView. On the United States storefront Apple permits apps
 * to link out to the developer's website for purchases with no entitlement and
 * no system disclosure sheet (App Store Review Guidelines 3.1.1(a) and 3.1.3(a),
 * post-2025 injunction), so instead of hiding the purchase surface entirely we
 * point users to raspicks.com to subscribe in their browser.
 *
 * The link MUST open in the system default browser (Safari), not the in-app
 * WebView. `target="_blank"` on an off-origin https URL makes Capacitor hand the
 * navigation to the system browser rather than loading it in the WebView, so a
 * plain anchor is sufficient here (no @capacitor/browser plugin, avoiding the
 * declared-but-not-installed web-drift blank-page hazard).
 *
 * Render this only on iOS native (US storefront); web and Android keep the
 * existing in-app `/order` checkout flow.
 */
export default function SubscribeExternalLink({
  path,
  className,
  children,
  'aria-label': ariaLabel,
}: Props) {
  return (
    <a
      href={subscribeWebUrl(path)}
      target="_blank"
      rel="noreferrer"
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </a>
  );
}
